import { computed, onMounted } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useFeatureFlagsStore } from '@/stores/featureFlags'

/**
 * Composable for feature flag operations
 * Provides convenient access to feature flags store with reactive values
 */
export function useFeatureFlag() {
  const featureFlagsStore = useFeatureFlagsStore()

  // Initialize flags on first use if not already loaded
  onMounted(() => {
    if (!featureFlagsStore.initialized && !featureFlagsStore.loading) {
      featureFlagsStore.fetchFlags().catch((err) => {
        console.error('Failed to load feature flags:', err)
      })
    }
  })

  // Computed properties
  const loading: Ref<boolean> = computed(() => featureFlagsStore.loading)
  const error: Ref<string | null> = computed(() => featureFlagsStore.error)

  /**
   * Check if a feature flag is enabled
   * @param flagKey - The key of the feature flag
   * @returns Computed boolean indicating if the flag is enabled
   */
  function isEnabled(flagKey: string): ComputedRef<boolean> {
    return computed(() => featureFlagsStore.isEnabled(flagKey))
  }

  /**
   * Get the value of a feature flag
   * @param flagKey - The key of the feature flag
   * @param defaultValue - The default value to return if flag is disabled or not found
   * @returns Computed value of the flag or default value
   */
  function getValue<T>(flagKey: string, defaultValue: T): ComputedRef<T> {
    return computed(() => featureFlagsStore.getValue(flagKey, defaultValue))
  }

  /**
   * Manually refresh feature flags from the server
   */
  async function refresh(): Promise<void> {
    await featureFlagsStore.fetchFlags()
  }

  /**
   * Clear any error state
   */
  function clearError(): void {
    featureFlagsStore.clearError()
  }

  return {
    // State
    loading,
    error,
    // Methods
    isEnabled,
    getValue,
    refresh,
    clearError,
  }
}
